import { AlertOutlined, BarChartOutlined, CalendarOutlined, CheckCircleOutlined, DashboardOutlined, DollarOutlined, FileTextOutlined, HeartOutlined, MailOutlined, ScheduleOutlined, SettingOutlined, ShopOutlined, TeamOutlined, ThunderboltOutlined } from "@ant-design/icons";
import type React from "react";
import type { PermissionEditorLevel } from "../../../../../../shared/frontend/permissions/PermissionEditor";
import { Permissions } from "../constants/permissions";
import type { LocalizedText } from "../i18n/i18n";

/**
 * Einzige Definition der Rechtezeilen (plans/2026-10-31-rechtesystem-neuordnung.md, Abschnitt 2).
 * Eine Gruppe je Menübereich, eine Zeile je Menüpunkt bzw. Einstellungsseite. Vorgang und
 * Einstellung sind getrennte Zeilen. Der Rolleneditor rendert diese Zeilen; Menü, Übersichtskarten
 * und Router-Guards verweisen über `row` auf sie, statt eigene Rechtelisten zu führen.
 */
export type PermissionLevel = { value: PermissionEditorLevel; labelKey: LocalizedText; permissions: string[]; descriptionKey?: LocalizedText };
export type PermissionExtra = { key: string; labelKey: LocalizedText; descriptionKey?: LocalizedText; requires?: string };
export type PermissionRow = {
  key: string;
  labelKey: LocalizedText;
  descriptionKey?: LocalizedText;
  levels: PermissionLevel[];
  extras?: PermissionExtra[];
};
export type PermissionGroup = {
  key: string;
  titleKey: LocalizedText;
  /** Einstellungsgruppe: Der Editor zeigt „Einstellungen · Titel“ wie im Menü. */
  settings?: boolean;
  icon: React.ReactNode;
  rows: PermissionRow[];
};

export const noAccessLevel: PermissionLevel = { value: "none", labelKey: "No access", permissions: [] };

function readOnlyLevels(...read: string[]): PermissionLevel[] {
  return [noAccessLevel, { value: "read", labelKey: "View", permissions: read }];
}

function writeLevels(read: string | string[], write: string | string[]): PermissionLevel[] {
  const r = Array.isArray(read) ? read : [read];
  const w = Array.isArray(write) ? write : [write];
  return [
    noAccessLevel,
    { value: "read", labelKey: "View", permissions: r },
    { value: "update", labelKey: "Edit", permissions: [...r, ...w] }
  ];
}

function createLevels(read: string, update: string, create: string): PermissionLevel[] {
  return [
    noAccessLevel,
    { value: "read", labelKey: "View", permissions: [read] },
    { value: "update", labelKey: "Edit", permissions: [read, update] },
    { value: "manage", labelKey: "Create permission level", permissions: [read, update, create] }
  ];
}

function manageOnlyLevels(permission: string): PermissionLevel[] {
  return [noAccessLevel, { value: "manage", labelKey: "Manage", permissions: [permission] }];
}

function settingsRow(key: string, labelKey: LocalizedText, read: string | string[], write: string | string[], descriptionKey?: LocalizedText): PermissionRow {
  return { key, labelKey, descriptionKey, levels: writeLevels(read, write) };
}

export const permissionGroups: PermissionGroup[] = [
  {
    key: "overview",
    titleKey: "Dashboard & Check-in",
    icon: <DashboardOutlined />,
    rows: [
      { key: "dashboard", labelKey: "Navigation.overview", descriptionKey: "Dashboard permission help", levels: readOnlyLevels(Permissions.DashboardView) },
      { key: "checkin", labelKey: "Check-in", descriptionKey: "Check-in permission help", levels: readOnlyLevels(Permissions.CheckinUse) }
    ]
  },
  {
    key: "tasks",
    titleKey: "Tasks",
    icon: <CheckCircleOutlined />,
    rows: [
      {
        key: "tasks",
        labelKey: "Tasks",
        descriptionKey: "Tasks permission help",
        levels: [
          noAccessLevel,
          { value: "read", labelKey: "View", permissions: [Permissions.TasksRead] },
          { value: "manage", labelKey: "Create permission level", permissions: [Permissions.TasksRead, Permissions.TasksCreate] }
        ],
        extras: [{ key: Permissions.TasksComplete, labelKey: "Complete tasks", descriptionKey: "Complete tasks help" }]
      }
    ]
  },
  {
    key: "persons",
    titleKey: "People",
    icon: <TeamOutlined />,
    rows: [
      {
        key: "persons",
        labelKey: "People",
        descriptionKey: "People permission help",
        levels: createLevels(Permissions.PersonsRead, Permissions.PersonsUpdate, Permissions.PersonsCreate),
        extras: [
          { key: Permissions.PersonsDelete, labelKey: "Delete people", descriptionKey: "Delete people help" },
          { key: Permissions.PersonsChangeHomeStudio, labelKey: "Change home studio", descriptionKey: "Change home studio help" },
          { key: Permissions.PersonsBlockCheckIn, labelKey: "Block/unblock access", descriptionKey: "Block check-in help" },
          { key: Permissions.PersonsPrivacyManage, labelKey: "Manage privacy", descriptionKey: "Manage privacy help" }
        ]
      },
      { key: "accessMedia", labelKey: "Access media", descriptionKey: "Access media permission help", levels: writeLevels(Permissions.AccessMediaRead, Permissions.AccessMediaWrite) },
      { key: "persons.documents", labelKey: "Digital file", descriptionKey: "Digital file permission help", levels: writeLevels(Permissions.PersonsDocumentsRead, Permissions.PersonsDocumentsWrite) },
      {
        key: "persons.changeHistory",
        labelKey: "Change history",
        descriptionKey: "Change history permission help",
        levels: readOnlyLevels(Permissions.PersonsChangeHistoryRead),
        extras: [{ key: Permissions.PersonsChangeHistoryUndo, labelKey: "Undo change history entries", descriptionKey: "Undo change history entries help", requires: Permissions.PersonsChangeHistoryRead }]
      }
    ]
  },
  {
    key: "contracts",
    titleKey: "Contracts",
    icon: <FileTextOutlined />,
    rows: [
      {
        key: "contracts",
        labelKey: "Contracts",
        descriptionKey: "Contracts permission help",
        levels: writeLevels(Permissions.ContractsRead, Permissions.ContractsUpdate),
        extras: [
          { key: Permissions.ContractsDelete, labelKey: "Delete contracts", descriptionKey: "Delete contracts help" },
          { key: Permissions.ContractsCancel, labelKey: "Cancel contracts", descriptionKey: "Cancel contracts help" },
          { key: Permissions.ContractsCancelCustomize, labelKey: "Customize contract cancellations", descriptionKey: "Customize contract cancellations help", requires: Permissions.ContractsCancel }
        ]
      },
      {
        key: "restPeriods",
        labelKey: "Rest periods",
        descriptionKey: "Rest periods permission help",
        levels: writeLevels(Permissions.RestPeriodsRead, Permissions.RestPeriodsWrite),
        extras: [{ key: Permissions.RestPeriodsCustomize, labelKey: "Customize rest periods", descriptionKey: "Customize rest periods help", requires: Permissions.RestPeriodsWrite }]
      },
      {
        key: "priceAdjustments",
        labelKey: "Price adjustments",
        descriptionKey: "Price adjustments permission help",
        levels: writeLevels(Permissions.PriceAdjustmentsRead, Permissions.PriceAdjustmentsWrite),
        extras: [{ key: Permissions.PriceAdjustmentsCustomize, labelKey: "Customize price adjustments", descriptionKey: "Customize price adjustments help", requires: Permissions.PriceAdjustmentsWrite }]
      }
    ]
  },
  {
    key: "sales",
    titleKey: "Sales & Leads",
    icon: <ShopOutlined />,
    rows: [
      {
        key: "leads",
        labelKey: "Leads",
        descriptionKey: "Leads permission help",
        levels: writeLevels(Permissions.LeadsRead, Permissions.LeadsWrite),
        extras: [{ key: Permissions.LeadsDelete, labelKey: "Delete leads", descriptionKey: "Delete leads help" }]
      },
      { key: "contractClosing", labelKey: "Close contract", descriptionKey: "Close contract permission help", levels: manageOnlyLevels(Permissions.ContractsClose) },
      { key: "articleSales", labelKey: "Article sales", descriptionKey: "Article sales permission help", levels: writeLevels(Permissions.ArticleSalesRead, Permissions.ArticleSalesWrite) }
    ]
  },
  {
    key: "memberCare",
    titleKey: "Member care",
    icon: <HeartOutlined />,
    rows: [
      {
        key: "memberCare",
        labelKey: "Member care cases",
        descriptionKey: "Member care permission help",
        levels: writeLevels(Permissions.MemberCareRead, Permissions.MemberCareWrite),
        extras: [{ key: Permissions.MemberCareDelete, labelKey: "Delete member care cases", descriptionKey: "Delete member care cases help" }]
      }
    ]
  },
  {
    key: "communication",
    titleKey: "Communication",
    icon: <MailOutlined />,
    rows: [
      { key: "communication.send", labelKey: "Sending", descriptionKey: "Communication send permission help", levels: readOnlyLevels(Permissions.CommunicationSend) },
      {
        key: "communication.outbound",
        labelKey: "Outbound communication",
        descriptionKey: "Outbound communication permission help",
        levels: createLevels(Permissions.CommunicationOutboundRead, Permissions.CommunicationOutboundWrite, Permissions.CommunicationOutboundCreate),
        extras: [
          { key: Permissions.CommunicationOutboundReview, labelKey: "Review outbound communication", descriptionKey: "Review outbound communication help" },
          { key: Permissions.CommunicationOutboundReviewOwn, labelKey: "Review own outbound communication", descriptionKey: "Review own outbound communication help" },
          { key: Permissions.CommunicationOutboundSkipReview, labelKey: "Skip outbound review", descriptionKey: "Skip outbound review help" },
          { key: Permissions.CommunicationOutboundCancel, labelKey: "Cancel outbound communication", descriptionKey: "Cancel outbound communication help" },
          { key: Permissions.CommunicationOutboundRetry, labelKey: "Retry outbound communication", descriptionKey: "Retry outbound communication help" }
        ]
      },
      {
        key: "communication.inbox",
        labelKey: "Inbox",
        descriptionKey: "Inbox permission help",
        levels: readOnlyLevels(Permissions.CommunicationInboxRead),
        extras: [{ key: Permissions.CommunicationInboxReview, labelKey: "Review inbox classifications", descriptionKey: "Review inbox classifications permission help" }]
      }
    ]
  },
  {
    key: "calendar",
    titleKey: "Calendar & Appointments",
    icon: <CalendarOutlined />,
    rows: [
      {
        key: "appointments",
        labelKey: "Appointments",
        descriptionKey: "Appointments permission help",
        levels: writeLevels(Permissions.AppointmentsRead, Permissions.AppointmentsWrite),
        extras: [
          { key: Permissions.AppointmentsCreateRequireConfirmation, labelKey: "Create appointment outside availability", descriptionKey: "Allows creating appointments outside stored resource or staff availability." },
          { key: Permissions.AppointmentsOverbook, labelKey: "Allow overlapping appointment bookings", descriptionKey: "Allows booking appointments despite overlaps or insufficient resource capacity." },
          { key: Permissions.AppointmentsEditDuration, labelKey: "Edit appointment duration", descriptionKey: "Allows changing the appointment duration instead of using the duration from the appointment type." },
          { key: Permissions.AppointmentsEditPrice, labelKey: "Edit appointment price", descriptionKey: "Allows changing the appointment price instead of using the appointment type price." }
        ]
      }
    ]
  },
  {
    key: "staff",
    titleKey: "Staff",
    icon: <ScheduleOutlined />,
    rows: [
      settingsRow("staff.schedule", "Staff schedule", Permissions.StaffScheduleRead, Permissions.StaffScheduleWrite, "Staff schedule permission help"),
      settingsRow("staff.absences", "Absences and availabilities", Permissions.StaffAbsencesRead, Permissions.StaffAbsencesWrite, "Staff absences permission help"),
      settingsRow("staff.vacation", "Vacation management", Permissions.StaffVacationRead, Permissions.StaffVacationWrite, "Vacation management permission help"),
      {
        key: "staff.time",
        labelKey: "Working time",
        descriptionKey: "Working time permission help",
        levels: writeLevels(Permissions.StaffTimeRead, Permissions.StaffTimeWrite),
        extras: [{ key: Permissions.StaffTimeApprove, labelKey: "Approve working time", descriptionKey: "Approve working time help" }]
      },
      {
        key: "staff.payroll",
        labelKey: "Payroll and hour balance",
        descriptionKey: "Payroll permission help",
        levels: readOnlyLevels(Permissions.StaffPayrollRead),
        extras: [{ key: Permissions.StaffPayrollExport, labelKey: "Export payroll", descriptionKey: "Export payroll help", requires: Permissions.StaffPayrollRead }]
      }
    ]
  },
  {
    key: "finance",
    titleKey: "Finance",
    icon: <DollarOutlined />,
    rows: [
      { key: "receivables", labelKey: "Receivables", descriptionKey: "Receivables permission help", levels: readOnlyLevels(Permissions.ReceivablesRead) },
      settingsRow("bankStatement", "Bank statement", Permissions.BankStatementRead, Permissions.BankStatementWrite, "Bank statement permission help"),
      {
        key: "openItems",
        labelKey: "Open items",
        descriptionKey: "Open items permission help",
        levels: writeLevels(Permissions.OpenItemsRead, Permissions.OpenItemsWrite),
        extras: [{ key: Permissions.OpenItemsAgreeInstallment, labelKey: "Agree installment plan", descriptionKey: "Agree installment plan help" }]
      },
      {
        key: "payments",
        labelKey: "Payments",
        descriptionKey: "Payments permission help",
        levels: writeLevels(Permissions.PaymentsRead, Permissions.PaymentsWrite),
        extras: [{ key: Permissions.PaymentsCancel, labelKey: "Cancel payments", requires: Permissions.PaymentsWrite }]
      },
      {
        key: "sepa",
        labelKey: "SEPA direct debits",
        descriptionKey: "SEPA direct debits permission help",
        levels: writeLevels(Permissions.SepaRead, Permissions.SepaWrite),
        extras: [{ key: Permissions.SepaSubmit, labelKey: "Submit and book direct debit runs", requires: Permissions.SepaWrite }]
      },
      settingsRow("reminders", "Reminders", Permissions.RemindersRead, Permissions.RemindersWrite, "Reminders permission help"),
      settingsRow("debtCollectionCases", "Debt collection cases", Permissions.DebtCollectionCasesRead, Permissions.DebtCollectionCasesWrite, "Debt collection cases permission help"),
      { key: "accountingExports", labelKey: "Accounting exports", descriptionKey: "Accounting exports permission help", levels: readOnlyLevels(Permissions.AccountingExportsRead) }
    ]
  },
  {
    key: "reports",
    titleKey: "Reports",
    icon: <BarChartOutlined />,
    rows: [
      { key: "reports.lists", labelKey: "Custom lists", descriptionKey: "Report lists permission help", levels: readOnlyLevels(Permissions.ReportsListsRead) },
      { key: "reports.members", labelKey: "Member reports", descriptionKey: "Member reports permission help", levels: readOnlyLevels(Permissions.ReportsMembersRead) },
      { key: "reports.finances", labelKey: "Finance reports", descriptionKey: "Finance reports permission help", levels: readOnlyLevels(Permissions.ReportsFinancesRead) },
      { key: "reports.contracts", labelKey: "Contract reports", descriptionKey: "Contract reports permission help", levels: readOnlyLevels(Permissions.ReportsContractsRead) },
      { key: "reports.usage", labelKey: "Usage reports", descriptionKey: "Usage reports permission help", levels: readOnlyLevels(Permissions.ReportsUsageRead) },
      { key: "reports.calendar", labelKey: "Calendar reports", descriptionKey: "Calendar reports permission help", levels: readOnlyLevels(Permissions.ReportsCalendarRead) },
      { key: "reports.leads", labelKey: "Lead reports", descriptionKey: "Lead reports permission help", levels: readOnlyLevels(Permissions.ReportsLeadsRead) },
      { key: "reports.memberCare", labelKey: "Member care reports", descriptionKey: "Member care reports permission help", levels: readOnlyLevels(Permissions.ReportsMemberCareRead) },
      { key: "reports.export", labelKey: "Export and print reports", descriptionKey: "Export and print reports permission help", levels: readOnlyLevels(Permissions.ReportsExport) }
    ]
  },
  {
    key: "tools",
    titleKey: "Tools",
    icon: <ThunderboltOutlined />,
    rows: [
      {
        key: "automation",
        labelKey: "Automations",
        descriptionKey: "Automations permission help",
        levels: [
          noAccessLevel,
          { value: "read", labelKey: "View", permissions: [Permissions.AutomationRead] },
          { value: "manage", labelKey: "Manage", permissions: [Permissions.AutomationRead, Permissions.AutomationManage] }
        ],
        extras: [
          { key: Permissions.AutomationExecute, labelKey: "Run automations", descriptionKey: "Run automations help" },
          { key: Permissions.AutomationApprove, labelKey: "Approve automations", descriptionKey: "Approve automations help" },
          { key: Permissions.AutomationMembers, labelKey: "Member automations", descriptionKey: "Member automations help" },
          { key: Permissions.AutomationFinance, labelKey: "Finance automations", descriptionKey: "Finance automations help" },
          { key: Permissions.AutomationContracts, labelKey: "Contract automations", descriptionKey: "Contract automations help" },
          { key: Permissions.AutomationOperations, labelKey: "Operations automations", descriptionKey: "Operations automations help" }
        ]
      },
      {
        key: "memberPromotions",
        labelKey: "Promotions and bonuses",
        descriptionKey: "Promotions permission help",
        levels: writeLevels(Permissions.MemberPromotionsRead, Permissions.MemberPromotionsWrite),
        extras: [{ key: Permissions.MemberPromotionsExecute, labelKey: "Execute promotions and bonuses", descriptionKey: "Execute promotions and bonuses help" }]
      },
      {
        key: "duplicateFinder",
        labelKey: "Duplicate finder",
        descriptionKey: "Duplicate finder permission help",
        levels: [
          noAccessLevel,
          { value: "read", labelKey: "View", permissions: [Permissions.DuplicateFinderScan] },
          { value: "manage", labelKey: "Manage", permissions: [Permissions.DuplicateFinderScan, Permissions.DuplicateFinderMerge] }
        ]
      }
    ]
  },
  {
    key: "logs",
    titleKey: "Logs",
    icon: <AlertOutlined />,
    rows: [
      {
        key: "logs",
        labelKey: "Logs",
        descriptionKey: "View logs description",
        levels: [
          noAccessLevel,
          { value: "read", labelKey: "View", permissions: [Permissions.LogsRead] },
          { value: "manage", labelKey: "Manage", permissions: [Permissions.LogsRead, Permissions.LogsWrite], descriptionKey: "Delete logs description" }
        ]
      }
    ]
  },
  {
    key: "settings.organisation",
    titleKey: "Organization & Users",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("masterData", "Master data", Permissions.MasterDataRead, Permissions.MasterDataWrite, "Master data permission help"),
      settingsRow("legalTexts", "Legal texts", Permissions.LegalTextsRead, Permissions.LegalTextsWrite),
      settingsRow("users", "Users", Permissions.UsersRead, Permissions.UsersWrite, "Users permission help"),
      settingsRow("roles", "Roles", Permissions.RolesRead, Permissions.RolesWrite, "Roles permission help"),
      settingsRow("billing", "Billing", Permissions.BillingRead, Permissions.BillingWrite)
    ]
  },
  {
    key: "settings.members",
    titleKey: "Member management",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("personDefaults", "Member defaults", Permissions.PersonDefaultsRead, Permissions.PersonDefaultsWrite, "Member defaults permission help"),
      settingsRow("checkinView", "Check-in view settings", Permissions.CheckinViewRead, Permissions.CheckinViewWrite, "Check-in view settings permission help")
    ]
  },
  {
    key: "settings.products",
    titleKey: "Products & Contracts",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("contractPackages", "Contract packages", Permissions.ContractPackagesRead, Permissions.ContractPackagesWrite, "Contract packages permission help"),
      settingsRow("contractComponents", "Contract components", Permissions.ContractComponentsRead, Permissions.ContractComponentsWrite, "Contract components permission help"),
      settingsRow("paymentPlans", "Payment plans", Permissions.PaymentPlansRead, Permissions.PaymentPlansWrite, "Payment plans permission help"),
      settingsRow("walletProducts", "Wallet products", Permissions.WalletProductsRead, Permissions.WalletProductsWrite, "Wallet products permission help"),
      settingsRow("articles", "Articles", Permissions.ArticlesRead, Permissions.ArticlesWrite, "Articles permission help"),
      settingsRow("restPeriodModels", "Rest period models and reasons", Permissions.RestPeriodModelsRead, Permissions.RestPeriodModelsWrite),
      settingsRow("cancellationModels", "Cancellation models and reasons",
        [Permissions.CancellationModelsRead, Permissions.CancellationReasonsRead],
        [Permissions.CancellationModelsWrite, Permissions.CancellationReasonsWrite]),
      settingsRow("priceAdjustmentModels", "Price adjustment models", Permissions.PriceAdjustmentModelsRead, Permissions.PriceAdjustmentModelsWrite),
      settingsRow("contractOptionGroups", "Option groups", Permissions.ContractOptionGroupsRead, Permissions.ContractOptionGroupsWrite, "Option groups permission help"),
      settingsRow("salesPromotions", "Sales promotions", Permissions.SalesPromotionsRead, Permissions.SalesPromotionsWrite, "Sales promotions permission help"),
      settingsRow("memberPromotionTemplates", "Promotion templates", Permissions.MemberPromotionTemplatesRead, Permissions.MemberPromotionTemplatesWrite)
    ]
  },
  {
    key: "settings.payments",
    titleKey: "Payments & Billing",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("accounting", "Accounting and cash registers", Permissions.AccountingRead, Permissions.AccountingWrite, "Accounting settings permission help"),
      settingsRow("bankAccounts", "Bank account settings", Permissions.BankAccountsRead, Permissions.BankAccountsWrite, "Bank account settings permission help"),
      settingsRow("defaultWalletPayment", "Default wallet payment settings", Permissions.DefaultWalletPaymentRead, Permissions.DefaultWalletPaymentWrite, "Default wallet payment settings permission help"),
      settingsRow("paymentMethods", "Payment methods settings", Permissions.PaymentMethodsRead, Permissions.PaymentMethodsWrite, "Payment methods settings permission help"),
      settingsRow("cancellationReasonsFinance", "Payment and contribution cancellation reasons",
        [Permissions.PaymentCancellationReasonsRead, Permissions.ContributionCancellationReasonsRead],
        [Permissions.PaymentCancellationReasonsWrite, Permissions.ContributionCancellationReasonsWrite]),
      settingsRow("debtManagement", "Debt management settings", Permissions.DebtManagementRead, Permissions.DebtManagementWrite, "Debt management settings permission help")
    ]
  },
  {
    key: "settings.contractSigning",
    titleKey: "Close contract",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("contractSigningWidgets", "Contract signing widgets", Permissions.ContractSigningWidgetsRead, Permissions.ContractSigningWidgetsWrite, "Contract signing widgets permission help")
    ]
  },
  {
    key: "settings.leads",
    titleKey: "Leads",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("leadSources", "Lead sources settings", Permissions.LeadSourcesRead, Permissions.LeadSourcesWrite, "Lead sources settings permission help"),
      settingsRow("leadPipelines", "Lead pipelines settings", Permissions.LeadPipelinesRead, Permissions.LeadPipelinesWrite, "Lead pipelines settings permission help"),
      settingsRow("leadWidgets", "Lead widgets settings", Permissions.LeadWidgetsRead, Permissions.LeadWidgetsWrite, "Lead widgets settings permission help")
    ]
  },
  {
    key: "settings.memberCare",
    titleKey: "Member care settings",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("memberCarePipelines", "Member care pipelines settings", Permissions.MemberCarePipelinesRead, Permissions.MemberCarePipelinesWrite, "Member care pipelines settings permission help")
    ]
  },
  {
    key: "settings.communication",
    titleKey: "Communication",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      {
        key: "templates",
        labelKey: "Templates",
        descriptionKey: "Templates permission help",
        levels: writeLevels(Permissions.TemplatesRead, Permissions.TemplatesWrite),
        extras: [{ key: Permissions.TemplatesAppearanceOnSend, labelKey: "Choose document appearance when sending", descriptionKey: "Choose document appearance when sending help" }]
      },
      settingsRow("documentAppearance", "Document appearance", Permissions.DocumentAppearanceRead, Permissions.DocumentAppearanceWrite),
      settingsRow("emailSettings", "Email settings", Permissions.EmailSettingsRead, Permissions.EmailSettingsWrite, "Email settings permission help"),
      settingsRow("outboundSettings", "Dispatch settings", Permissions.CommunicationOutboundSettingsRead, Permissions.CommunicationOutboundSettings, "Manage outbound settings help")
    ]
  },
  {
    key: "settings.calendar",
    titleKey: "Calendar & Appointments",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("appointmentConfig", "Appointment configuration", Permissions.AppointmentConfigRead, Permissions.AppointmentConfigWrite, "Appointment configuration permission help"),
      settingsRow("appointmentTypes", "Appointment types", Permissions.AppointmentTypesRead, Permissions.AppointmentTypesWrite, "Appointment types permission help"),
      settingsRow("resources", "Resources settings", Permissions.ResourcesRead, Permissions.ResourcesWrite, "Resources settings permission help"),
      settingsRow("resourceUnavailabilities", "Unavailabilities settings", Permissions.ResourceUnavailabilitiesRead, Permissions.ResourceUnavailabilitiesWrite, "Unavailabilities settings permission help"),
      settingsRow("availabilities", "Availabilities", Permissions.AvailabilitiesRead, Permissions.AvailabilitiesWrite, "Availabilities permission help"),
      settingsRow("appointmentOutcomes", "Appointment outcomes", Permissions.AppointmentOutcomesRead, Permissions.AppointmentOutcomesWrite, "Appointment outcomes permission help"),
      settingsRow("bookingWidgets", "Booking widgets", Permissions.BookingWidgetsRead, Permissions.BookingWidgetsWrite, "Booking widgets permission help"),
      settingsRow("appointmentNotifications", "Appointment notifications", Permissions.AppointmentNotificationsRead, Permissions.AppointmentNotificationsWrite, "Appointment notifications permission help")
    ]
  },
  {
    key: "settings.staff",
    titleKey: "Staff",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("staff.settings", "Staff settings", Permissions.StaffSettingsRead, Permissions.StaffSettingsWrite, "Staff settings permission help")
    ]
  },
  {
    key: "settings.access",
    titleKey: "Access & Devices",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("readerDevices", "Reader devices", Permissions.ReaderDevicesRead, Permissions.ReaderDevicesWrite, "Reader devices permission help"),
      settingsRow("memberApp", "Studio app settings", Permissions.MemberAppRead, Permissions.MemberAppWrite, "Studio app settings permission help")
    ]
  },
  {
    key: "settings.system",
    titleKey: "Navigation.general",
    settings: true,
    icon: <SettingOutlined />,
    rows: [
      settingsRow("units", "Units settings", Permissions.UnitsRead, Permissions.UnitsWrite, "Units settings permission help"),
      settingsRow("defaultTheme", "Default theme settings", Permissions.DefaultThemeRead, Permissions.DefaultThemeWrite, "Default theme settings permission help"),
      settingsRow("userPreferences", "User templates settings", Permissions.UserPreferencesRead, Permissions.UserPreferencesWrite, "User templates settings permission help"),
      settingsRow("integrations", "Integrations settings", Permissions.IntegrationsRead, Permissions.IntegrationsWrite, "Integrations settings permission help")
    ]
  }
];

export function rowPermissionKeys(row: PermissionRow): string[] {
  return Array.from(new Set([...row.levels.flatMap((level) => level.permissions), ...(row.extras || []).map((extra) => extra.key)]));
}

export function groupPermissionKeys(group: PermissionGroup): string[] {
  return Array.from(new Set(group.rows.flatMap(rowPermissionKeys)));
}

const rowByKey = new Map(permissionGroups.flatMap((g) => g.rows.map((r) => [r.key, r] as const)));

export type PermissionRowKey = string;

export function permissionRow(key: PermissionRowKey): PermissionRow {
  const row = rowByKey.get(key);
  if (!row) throw new Error(`Unbekannte Rechtezeile: ${key}`);
  return row;
}

/**
 * Schlüssel, von denen einer genügt, um die Seite einer Zeile zu öffnen (Menü, Karten, Guards):
 * jede Stufe ab „lesen“. Sonderaktionen öffnen keine Seite.
 */
export function rowAccessPermissions(key: PermissionRowKey): string[] {
  return Array.from(new Set(permissionRow(key).levels.flatMap((level) => level.permissions)));
}

/** Alle Schlüssel, die zu einer Rollenzeile gehören (Editor-Gruppenschalter). */
export const allPermissionRowKeys: string[] = Array.from(new Set(permissionGroups.flatMap(groupPermissionKeys)));
